import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, finalize, take, takeUntil } from 'rxjs';
import {
  CategoryExpenseFields,
  INVOICE_STATUS,
  InvoiceListResponse,
  MultipleAccountsRelationship,
  MultipleAccountsRelationshipServiceIdentity,
  PcpCarouselComponent,
  PcpHeaderMultipleAccountsRelationshipComponent,
  PcpInvoiceSummaryComponent,
} from '@celerofinancas/pcp-client-ui';
import { CdsSimpleBarChartModule, SimpleBarChartItem } from '@celerofinancas/ui-chart';
import { TooltipDirective } from '@celerofinancas/ui-directives';
import { TRACKING_MODELS, TrackingService } from '@celerofinancas/service-utils';
import { ModalCloseEvent, ModalService } from '@celerofinancas/ui-modals';
import { MoneyPipe } from '@celerofinancas/ui-pipes';
import { CREDIT_CARD_SUMMARY_TOKENS } from '../../../configs/credit-card-summary.tokens';
import { ShimmerComponent } from '@shared/components/shimmer/shimmer.component';
import { MainTitleComponent } from '@shared/components/main-title/main-title.component';
import { NavigationBarComponent } from '@shared/components/navigation-bar/navigation-bar.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { BootstrapCoordinator } from '@shared/models/bootstrap-coordinator';
import { TransactionsTableComponent } from '../transactions-table/transactions-table.component';
import {
  CreditCardsHttpDataSourceService,
  InvoiceListItem,
} from '../credit-cards-data-source.service';
import { CreditCardIdentityService } from '../credit-card-identity.service';
import { CreditCardsTableItem } from '../credit-cards-data-source.token';
import {
  EditContractedProductModalComponent,
  EditContractedProductModalResult,
} from '../edit-contracted-product-modal/edit-contracted-product-modal.component';
import {
  CATEGORY_PALETTE,
  CategoryLegendItem,
  MAX_LEGEND_ITEMS,
  OTHER_CATEGORIES_COLOR,
  UNCATEGORIZED_LABEL,
  UNCATEGORIZED_TOOLTIP,
} from '../credit-cards-data-interface';

/**
 * CreditCardSummaryComponent
 */
@Component({
  selector: 'caixa-webcomponent-credit-card-summary',
  standalone: true,
  templateUrl: './credit-card-summary.component.html',
  styleUrl: './credit-card-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MainTitleComponent,
    NavigationBarComponent,
    ShimmerComponent,
    EmptyStateComponent,
    PcpHeaderMultipleAccountsRelationshipComponent,
    PcpCarouselComponent,
    PcpInvoiceSummaryComponent,
    MoneyPipe,
    CdsSimpleBarChartModule,
    TooltipDirective,
    TransactionsTableComponent,
  ],
  providers: [...CREDIT_CARD_SUMMARY_TOKENS],
})
export class CreditCardSummaryComponent extends BootstrapCoordinator implements OnInit {
  /**
   * Whether the invoices are being fetched.
   */
  public readonly isLoadingInvoices = signal(false);

  /**
   * Bills fetched from API, mapped to InvoiceListResponse for the carousel.
   */
  public readonly invoices = signal<InvoiceListResponse[]>([]);

  /**
   * The currently selected invoice from the carousel. Null until bills are loaded.
   */
  public readonly selectedInvoice = signal<InvoiceListResponse | null>(null);

  /**
   * Whether the bills request failed. Drives the "Faturas do cartão" empty/error
   * state so a failed request shows a placeholder instead of a blank section.
   */
  public readonly invoicesError = signal(false);

  /**
   * Mapping of invoice status codes to their display labels. Used in the template to show human-readable status.
   */
  protected readonly invoiceStatusLabel = new Map<number, string>([
    [INVOICE_STATUS.open, 'Em aberto'],
    [INVOICE_STATUS.closed, 'Fechada'],
    [INVOICE_STATUS.overdue, 'Em atraso'],
    [INVOICE_STATUS.paid, 'Paga'],
    [INVOICE_STATUS.partially_paid, 'Paga parcialmente'],
    [INVOICE_STATUS.future, 'Futura'],
  ]);

  /**
   * Timestamp of the last data update, used to trigger re-rendering of child components when updated. Updated after editing the contracted product to ensure the header reflects changes.
   */
  public lastUpdate = signal(new Date());

  /**
   * Flag to control the visibility of the header component. Toggled to force re-rendering of the header after editing the contracted product, ensuring it reflects the latest data.
   */
  public showHeader = signal(true);

  /**
   * Categories breakdown streamed to the chart.
   */
  public readonly categories$ = new BehaviorSubject<CategoryExpenseFields[]>([]);

  /**
   * Total expenses streamed to the chart.
   */
  public readonly totalExpenses$ = new BehaviorSubject(0);

  /**
   * Loading state for categories request.
   */
  public readonly categoriesLoading$ = new BehaviorSubject(false);

  /**
   * Error state for categories request.
   */
  public readonly categoriesError$ = new BehaviorSubject(false);

  /**
   * Colored bar segments fed to the stacked bar chart.
   */
  public readonly barItems$ = new BehaviorSubject<SimpleBarChartItem[]>([]);

  /**
   * Legend rows rendered below the bar.
   */
  public readonly legendItems$ = new BehaviorSubject<CategoryLegendItem[]>([]);

  /**
   * Row count used by the legend's column-major grid layout so that the
   * sequence of legend cells matches the left-to-right order of the bar.
   */
  public readonly legendRows$ = new BehaviorSubject(1);

  /**
   * Bill id of the selected invoice, fed to the transactions table child.
   */
  public readonly selectedBillId = computed(
    () => (this.selectedInvoice() as InvoiceListItem | null)?.bill_id ?? '',
  );

  /**
   * Router instance for navigation. Used to navigate back to the credit cards list when the user clicks the return button.
   */
  private readonly router = inject(Router);

  /**
   * Identity service for the selected credit card. Provides access to the current card's data, which is used to populate the header and fetch related bills. Updated after editing the contracted product to ensure the header reflects changes.
   */
  private readonly creditCardIdentity = inject(CreditCardIdentityService);

  /**
   * Data source service for credit cards. Used to fetch bills related to the selected credit card and to update the contracted product nickname.
   */
  private readonly dataSource = inject(CreditCardsHttpDataSourceService);

  /**
   * Identity service for multiple accounts relationship. Used to set the current card's relationship data, which is displayed in the header, and to update it after editing the contracted product to ensure the header reflects changes.
   */
  private readonly multipleAccountsRelationshipIdentity = inject(
    MultipleAccountsRelationshipServiceIdentity,
  );

  /**
   * Modal service for opening modals. Used to open the edit modal for the contracted product nickname when the user clicks the edit button in the header. After a successful save, updates the identity and triggers a re-render of the header to reflect changes.
   */
  private readonly modalService = inject(ModalService);

  /**
   * Tracking service for chart hover events.
   */
  private readonly tracking = inject(TrackingService);

  /**
   * Tooltip text used by the legend's info icon.
   */
  protected readonly uncategorizedTooltip = UNCATEGORIZED_TOOLTIP;

  /**
   * Credit card account id captured once per loadBills call and reused by
   * loadCategories to avoid repeated identity lookups across carousel changes.
   */
  private creditCardAccountId = '';

  /**
   * Navigates back to the credit cards list page. Called when the user clicks the return button in the header.
   */
  public onReturn(): void {
    void this.router.navigate(['credit-cards']);
  }

  /**
   * Populates the header identity and fetches bills for the selected card.
   */
  public override ngOnInit(): void {
    super.ngOnInit();

    const card = this.creditCardIdentity.get();

    if (card) {
      this.multipleAccountsRelationshipIdentity.set(this.toRelationship(card));
      if (card.id) {
        this.loadBills(card.id);
      }
    }
  }

  /**
   * Opens the edit modal for the contracted product nickname.
   * After a successful save, updates the identity with the new name and re-renders the header.
   */
  public onEditProduct(element: unknown): void {
    if (!this.creditCardAccountId) {
      return;
    }

    const rel = element as MultipleAccountsRelationship;

    const modalRef = this.modalService.open<EditContractedProductModalResult>(
      EditContractedProductModalComponent,
      {
        id: this.creditCardAccountId,
        name: rel.product_tier ?? '',
        showHeader: false,
      },
      { modal: { closeStrategy: 'static' } },
    );

    modalRef.afterClosed$
      .pipe(take(1), takeUntil(this.destroyController$))
      .subscribe((event: ModalCloseEvent<EditContractedProductModalResult>) => {
        if (event.type !== 'close' || !event.data?.success) {
          return;
        }

        const updated: MultipleAccountsRelationship = {
          ...rel,
          product_tier: event.data.name ?? rel.product_tier,
        };

        this.multipleAccountsRelationshipIdentity.set(updated);

        this.showHeader.set(false);
        setTimeout(() => this.showHeader.set(true), 0);
      });
  }

  /**
   * Track hover on the categories expenses chart.
   */
  public trackCategoriesHover(): void {
    this.tracking.track(TRACKING_MODELS.creditCardHoverTotalSpendingByCategoryInCurrentMonthly);
  }

  /** Fetches categories breakdown for the given bill. */
  private loadCategories(billId: string): void {
    if (!billId) {
      this.publishCategories([], 0);
      this.categoriesError$.next(false);
      return;
    }

    this.categoriesLoading$.next(true);

    this.dataSource
      .listCategories(billId, this.creditCardAccountId)
      .pipe(
        finalize(() => this.categoriesLoading$.next(false)),
        takeUntil(this.destroyController$),
      )
      .subscribe({
        next: (result) => {
          this.publishCategories(result?.categories ?? [], result?.total_value ?? 0);
          this.categoriesError$.next(false);
        },
        error: () => {
          this.publishCategories([], 0);
          this.categoriesError$.next(true);
        },
      });
  }

  /**
   * Updates downstream streams (categories, total, bar items, legend items).
   *
   * @param categories raw categories from the data source
   * @param totalValue total expense value
   */
  private publishCategories(categories: CategoryExpenseFields[], totalValue: number): void {
    this.categories$.next(categories);
    this.totalExpenses$.next(totalValue);

    const { barItems, legendItems } = this.buildChartData(categories);
    this.barItems$.next(barItems);
    this.legendItems$.next(legendItems);
    this.legendRows$.next(Math.max(1, Math.ceil(legendItems.length / 3)));
  }

  /**
   * Builds the bar segments and legend rows from the API categories. Top N
   * categories are surfaced individually; the remainder is aggregated as
   * "Outras Categorias" using the neutral palette color.
   *
   * @param categories source categories
   * @returns bar items + legend items
   */
  private buildChartData(
    categories: CategoryExpenseFields[],
  ): { barItems: SimpleBarChartItem[]; legendItems: CategoryLegendItem[] } {
    if (!categories.length) {
      return { barItems: [], legendItems: [] };
    }

    const uncategorized = categories.find((category) => category.name === UNCATEGORIZED_LABEL);
    const named = categories
      .filter((category) => category.name !== UNCATEGORIZED_LABEL)
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

    const headLimit = Math.max(0, MAX_LEGEND_ITEMS - (uncategorized ? 1 : 0));
    const head = named.slice(0, headLimit);
    const tail = named.slice(headLimit);

    const legendItems: CategoryLegendItem[] = head.map((category, index) => ({
      name: category.name,
      value: category.value,
      percent: category.percent,
      color: CATEGORY_PALETTE[index % CATEGORY_PALETTE.length],
      showInfo: false,
    }));

    if (uncategorized) {
      legendItems.push({
        name: uncategorized.name,
        value: uncategorized.value,
        percent: uncategorized.percent,
        color: CATEGORY_PALETTE[legendItems.length % CATEGORY_PALETTE.length],
        showInfo: true,
      });
    }

    if (tail.length) {
      const aggregated = tail.reduce(
        (acc, category) => ({
          value: acc.value + (category.value ?? 0),
          percent: acc.percent + (category.percent ?? 0),
        }),
        { value: 0, percent: 0 },
      );

      legendItems.push({
        name: 'Outras Categorias',
        value: aggregated.value,
        percent: aggregated.percent,
        color: OTHER_CATEGORIES_COLOR,
        showInfo: false,
      });
    }

    const barItems: SimpleBarChartItem[] = legendItems.map((item) => ({
      name: item.name,
      value: item.value,
      percent: item.percent,
      color: item.color,
    }));

    return { barItems, legendItems };
  }

  /** Fetches and maps bills to InvoiceListResponse for the carousel. */
  private loadBills(creditCardAccountId: string): void {
    this.creditCardAccountId = creditCardAccountId;
    this.isLoadingInvoices.set(true);
    this.invoicesError.set(false);
    this.selectedInvoice.set(null);
    this.dataSource
      .listBills(creditCardAccountId)
      .pipe(
        finalize(() => this.isLoadingInvoices.set(false)),
        takeUntil(this.destroyController$),
      )
      .subscribe({
        next: (bills) => {
          this.invoices.set(bills);
          const firstBill = bills[0] ?? null;
          this.selectedInvoice.set(firstBill);
          const billId = (firstBill as InvoiceListItem | null)?.bill_id ?? '';
          this.loadCategories(billId);
        },
        error: () => {
          this.invoices.set([]);
          this.selectedInvoice.set(null);
          this.invoicesError.set(true);
          // Without bills there are no categories to show; reset the chart so the
          // categories section falls back to its own empty state instead of stale data.
          this.publishCategories([], 0);
          this.categoriesError$.next(false);
        },
      });
  }

  /** Converts a CreditCardsTableItem to a MultipleAccountsRelationship object. */
  private toRelationship(item: CreditCardsTableItem): MultipleAccountsRelationship {
    return {
      id: Number(item.id),
      card_brand: item.brand,
      product_tier: item.contractedProduct,
      issuer_bank_number: item.bank,
      total_limit: item.totalLimit ?? undefined,
      used_limit: item.usedLimit ?? undefined,
      available_limit: item.availableLimit ?? undefined,
      due_day: item.dueDay,
      cards: [],
    };
  }

  /**
   * Returns the color associated with a given invoice status.
   * @param status The status of the invoice.
   * @returns The color corresponding to the invoice status.
   */
  protected invoiceStatusColor(status: number | undefined): string {
    const colors = new Map<number, string>([
      [INVOICE_STATUS.open, 'c-info-500'],
      [INVOICE_STATUS.closed, 'c-foreground-400'],
      [INVOICE_STATUS.overdue, 'c-danger-500'],
      [INVOICE_STATUS.paid, 'c-success'],
      [INVOICE_STATUS.partially_paid, 'c-success'],
      [INVOICE_STATUS.future, 'c-warn-500'],
    ]);
    return colors.get(status ?? -1) ?? '';
  }

  /**
   * Handles the selection of an invoice from the carousel.
   * @param invoice The selected invoice.
   */
  public onSelectedInvoice(invoice: InvoiceListResponse): void {
    this.selectedInvoice.set(invoice);
    const billId = (invoice as InvoiceListItem).bill_id ?? '';
    this.loadCategories(billId);
  }
}
